import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getJob, commitJob, refineJob, discardJob } from "../../api/ingest.api";

const STATE_PILL = {
  UPLOADED:   "bg-slate-600/30 text-slate-300 border-slate-600/40",
  EXTRACTING: "bg-blue-500/20 text-blue-300 border-blue-500/40",
  REVIEW:     "bg-amber-500/20 text-amber-300 border-amber-500/40",
  COMMITTED:  "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  DISCARDED:  "bg-zinc-600/30 text-zinc-400 border-zinc-600/40",
  ERRORED:    "bg-rose-500/20 text-rose-300 border-rose-500/40",
};

const ROW_PILL = {
  NEW:      "bg-blue-500/20 text-blue-300",
  MATCH:    "bg-amber-500/20 text-amber-300",
  NO_DELTA: "bg-zinc-600/30 text-zinc-400",
};

export default function IngestJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refineMsg, setRefineMsg] = useState("");
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      const data = await getJob(jobId);
      setJob(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load job");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [jobId]);

  const handleCommit = async () => {
    if (!confirm("Commit this job? This will write to the live cricket DB.")) return;
    setBusy(true);
    try {
      const r = await commitJob(jobId);
      alert("Committed: " + JSON.stringify(r.summary || r, null, 2));
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRefine = async () => {
    if (!refineMsg.trim()) return;
    setBusy(true);
    try {
      await refineJob(jobId, refineMsg);
      setRefineMsg("");
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Refine failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!confirm("Discard this job?")) return;
    try {
      await discardJob(jobId);
      navigate("/ingest");
    } catch (e) {
      alert(e?.response?.data?.detail || "Discard failed");
    }
  };

  if (loading) return <div className="p-8 text-text-secondary">Loading…</div>;
  if (error || !job)
    return <div className="p-8 text-rose-400">{error || "Not found"}</div>;

  const isPlayerProfile = job.source_kind === "PLAYER_PROFILE";
  const players = isPlayerProfile ? (job.extracted?.players || []) : null;
  const summary = job.extracted?.summary;
  const matches = !isPlayerProfile ? (job.extracted?.matches || []) : null;

  return (
    <div className="min-h-screen bg-bg-main py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <Link to="/ingest" className="text-text-secondary text-sm hover:text-text-primary">
          ← Back to jobs
        </Link>

        <div className="flex items-start justify-between mt-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">{job.source_filename}</h1>
            <div className="flex items-center gap-3 mt-2 text-sm">
              <span className={`px-2 py-1 text-xs rounded border ${STATE_PILL[job.state]}`}>
                {job.state}
              </span>
              <span className="text-text-secondary">{job.source_kind}</span>
              <span className="text-text-secondary">${parseFloat(job.cost_usd || 0).toFixed(4)}</span>
              <span className="text-text-secondary">{job.model_used || "—"}</span>
            </div>
          </div>
          <div className="flex gap-3">
            {job.state === "REVIEW" && (
              <button
                onClick={handleCommit}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium"
              >
                Commit to DB
              </button>
            )}
            {job.state !== "COMMITTED" && job.state !== "DISCARDED" && (
              <button
                onClick={handleDiscard}
                className="bg-rose-600/20 hover:bg-rose-600/40 text-rose-300 border border-rose-500/40 px-5 py-2 rounded-lg font-medium"
              >
                Discard
              </button>
            )}
          </div>
        </div>

        {job.error && (
          <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg p-4 mb-6">
            <strong>Error:</strong> {job.error}
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {Object.entries(summary).map(([k, v]) => (
              <div key={k} className="bg-bg-primary border border-bg-secondary rounded-lg p-4">
                <p className="text-xs text-text-secondary uppercase tracking-wider">{k.replace(/_/g, " ")}</p>
                <p className="text-2xl font-bold text-text-primary">{v}</p>
              </div>
            ))}
          </div>
        )}

        {/* PLAYER_PROFILE table */}
        {isPlayerProfile && players && (
          <div className="bg-bg-primary border border-bg-secondary rounded-xl overflow-hidden mb-6">
            <table className="w-full text-left text-sm">
              <thead className="bg-bg-secondary/50 text-text-secondary text-xs uppercase">
                <tr>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">ci_id</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Country</th>
                  <th className="px-3 py-2">Gender</th>
                  <th className="px-3 py-2">DOB</th>
                  <th className="px-3 py-2">Bat</th>
                  <th className="px-3 py-2">Bowl</th>
                  <th className="px-3 py-2">Updates</th>
                </tr>
              </thead>
              <tbody>
                {players.slice(0, 200).map((p, i) => (
                  <tr key={p.ci_player_id + "-" + i} className="border-t border-bg-secondary/40">
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded ${ROW_PILL[p.status] || ""}`}>{p.status}</span>
                    </td>
                    <td className="px-3 py-2 text-text-secondary font-mono text-xs">{p.ci_player_id}</td>
                    <td className="px-3 py-2 text-text-primary">{p.full_name}</td>
                    <td className="px-3 py-2 text-text-secondary">{p.country}</td>
                    <td className="px-3 py-2 text-text-secondary">{p.gender}</td>
                    <td className="px-3 py-2 text-text-secondary text-xs">{p.born_date || "—"}</td>
                    <td className="px-3 py-2 text-text-secondary text-xs">{p.batting_style} {p.batting_hand && `(${p.batting_hand})`}</td>
                    <td className="px-3 py-2 text-text-secondary text-xs">{p.bowling_style} {p.bowling_hand && `(${p.bowling_hand})`}</td>
                    <td className="px-3 py-2 text-text-secondary text-xs">
                      {p.planned_updates && Object.keys(p.planned_updates).length
                        ? Object.keys(p.planned_updates).join(", ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {players.length > 200 && (
              <div className="p-3 text-center text-text-secondary text-xs border-t border-bg-secondary/40">
                Showing first 200 of {players.length}. Commit applies to all rows.
              </div>
            )}
          </div>
        )}

        {/* AI Match list */}
        {!isPlayerProfile && matches && matches.length > 0 && (
          <div className="space-y-4 mb-6">
            {matches.map((m, i) => (
              <div key={i} className="bg-bg-primary border border-bg-secondary rounded-xl p-4">
                <h3 className="text-text-primary font-semibold mb-1">
                  {m.team1} vs {m.team2} <span className="text-text-secondary text-sm font-normal">— {m.match_type} {m.match_date || ""}</span>
                </h3>
                <p className="text-text-secondary text-sm">
                  {m.competition || "—"} · {m.venue || "—"}
                </p>
                {m.result_text && (
                  <p className="text-text-secondary text-sm mt-1">Result: {m.result_text}</p>
                )}
                {m.innings?.length > 0 && (
                  <div className="mt-3 text-sm grid grid-cols-2 gap-3">
                    {m.innings.map((inn, j) => (
                      <div key={j} className="bg-bg-secondary/40 rounded p-2">
                        <p className="text-text-primary font-medium text-sm">
                          Innings {inn.innings_number}: {inn.batting_team}
                        </p>
                        <p className="text-text-secondary text-xs">
                          {inn.total_runs}/{inn.wickets} ({inn.overs} ov) — {(inn.batters || []).length} batters, {(inn.bowlers || []).length} bowlers
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Refine box (AI only) */}
        {!isPlayerProfile && job.state === "REVIEW" && (
          <div className="bg-bg-primary border border-bg-secondary rounded-xl p-4 mb-6">
            <h3 className="text-text-primary font-medium mb-2">Correction</h3>
            <p className="text-text-secondary text-sm mb-3">
              Tell Claude what's wrong (e.g. "that's the 2017 final, not 2018"). The extraction will be rebuilt with your correction applied.
            </p>
            <textarea
              rows={3}
              value={refineMsg}
              onChange={(e) => setRefineMsg(e.target.value)}
              disabled={busy}
              className="w-full bg-bg-secondary text-text-primary border border-bg-secondary rounded-lg px-3 py-2 mb-2"
            />
            <button
              onClick={handleRefine}
              disabled={busy || !refineMsg.trim()}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-medium"
            >
              Send correction
            </button>
          </div>
        )}

        {/* Player candidates (AI only) */}
        {job.candidates?.length > 0 && (
          <details className="bg-bg-primary border border-bg-secondary rounded-xl p-4 mb-6">
            <summary className="text-text-primary font-medium cursor-pointer">
              Player candidates ({job.candidates.length})
            </summary>
            <div className="mt-3 text-sm text-text-secondary">
              <p className="mb-2">
                These players were extracted but couldn't be unambiguously matched against the DB.
                Decide via the API for now (UI button per row coming next iteration).
              </p>
              <pre className="bg-bg-secondary/40 rounded p-3 overflow-auto text-xs">
                {JSON.stringify(job.candidates, null, 2)}
              </pre>
            </div>
          </details>
        )}

        <details className="bg-bg-primary border border-bg-secondary rounded-xl p-4">
          <summary className="text-text-secondary text-sm cursor-pointer">Raw extracted JSON</summary>
          <pre className="bg-bg-secondary/40 rounded p-3 mt-3 overflow-auto text-xs text-text-secondary max-h-96">
            {JSON.stringify(job.extracted, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
}
